/**
 * RAGFlow Conversation View
 *
 * Chat interface for RAGFlow-powered knowledge domains.
 * Uses RAGFlow API for retrieval and chat.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  SparklesIcon,
  ArrowLeftIcon,
  BugAntIcon,
} from '@heroicons/react/24/outline';
import { useToasts } from '../../stores/useToasts';
import { Spinner, Badge } from '../ui';
import { cn } from '../../shared/lib/cn';
import {
  ChatContainer,
  ChatMessagesPane,
  ChatScrollArea,
  ChatInputArea,
  MessageBubble,
  MessageContent,
  ThinkingIndicator,
  ChatProvider,
  CanvasPanel,
  useChat,
  type ExecutionStep,
} from '../ui/chat';
import { PdfCanvasViewer } from '../ui/pdf-canvas-viewer';
import { MemoryCompanionPanel } from './MemoryCompanionPanel';
import {
  WorkspaceChatArtifactBlocks,
  extractWorkspaceChatArtifactsFromMessage,
} from './WorkspaceChatArtifactBlocks';
import { ProviderAuthCard } from '../data-search/ProviderAuthCard';
import { PromptBar } from '../ui/prompt-bar';
import { InlineCitation } from '../ui/inline-citation';
import { apiUrl as buildApiUrl } from '../../services/api-client';
import {
  SourcesAccordion,
  parseSourcesFromSummary,
  buildSourcesFromChunks,
  type Source,
  type CitationValidationResult,
} from '../ui/sources-accordion';
import {
  applyAgentMeshLiveEventToSteps as reduceAgentMeshLiveEventToSteps,
  extractProviderAuthRequiredFromMessage,
  getFriendlyLiveProgressLabel as getAgentMeshFriendlyLiveProgressLabel,
  normalizeAssistantMarkdownContent as normalizeAgentMeshAssistantMarkdownContent,
  stripGfmTableBlocks,
} from './ragFlowConversationUtils';
import {
  getAgentMeshSourceDefinition,
  type AgentMeshSourceType
} from '../../lib/agent-mesh-sources';
import type { RuntimeProviderAuthRequired } from '../../lib/retrieval-runtime-auth';

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
const RAG_EVAL_API_BASE = buildApiUrl('/api/v1/rag-eval');

interface RetrievedChunk {
  content: string;
  document_name: string;
  similarity: number;
  kind?: string;
  metadata?: {
    source?: string;
    follow_ups?: string[];
    tool_calls?: AgentMeshToolCall[];
    failed_tools?: string[];
    sources?: string[];
    auth_required?: RuntimeProviderAuthRequired;
    provider_auth_required?: RuntimeProviderAuthRequired;
    resume_query?: string;
    [key: string]: any;
  };
}

interface AgentMeshToolCall {
  id?: string;
  tool?: string;
  action?: string | null;
  label?: string;
  status?: 'completed' | 'failed' | 'pending' | 'active';
  description?: string;
  error_code?: string;
  debug_dump?: {
    filename?: string;
    path?: string;
    dump_id?: string;
    timestamp?: string;
    retrieval_context?: {
      retrieval_backend?: string;
      retrieval_mode?: string;
      fallback_used?: boolean;
      fallback_reason?: string | null;
      selected_chunks?: Array<{
        fragment_id?: string;
        document_type?: string;
        canonical_name?: string;
        score?: number | null;
        match_reasons?: string[];
        source_authority?: string;
      }>;
      deterministic_expansion_reasons?: Array<{
        reason?: string;
        from?: string;
        added?: string[] | string;
      }>;
      verified_query_examples?: {
        included?: Array<Record<string, any>>;
        excluded?: Array<Record<string, any>>;
      };
    };
    [key: string]: any;
  };
  phase?: string;
  iteration?: number;
  step_index?: number;
}

interface AgentMeshMessageMeta {
  followUps: string[];
  toolCalls: AgentMeshToolCall[];
  failedTools: string[];
}

interface AgentMeshLiveEvent {
  event_id?: string;
  event_type?: string;
  message?: string;
  metadata?: Record<string, any>;
  sequence?: number;
  timestamp?: string;
}

const BASE_STEP_IDS = {
  submitted: 'submitted',
  planning: 'planning',
  tools: 'tools',
  generating: 'generating',
} as const;

function createInitialLiveSteps(): ExecutionStep[] {
  return [
    {
      id: BASE_STEP_IDS.submitted,
      label: 'Understanding your question',
      status: 'active',
    },
    {
      id: BASE_STEP_IDS.planning,
      label: 'Finding where the answer is',
      status: 'pending',
      description: 'Choosing the best sources to check first.',
    },
    {
      id: BASE_STEP_IDS.tools,
      label: 'Gathering details from your data',
      status: 'pending',
      description: 'Reading connected records and notes.',
    },
    {
      id: BASE_STEP_IDS.generating,
      label: 'Writing your answer',
      status: 'pending',
      description: 'Combining findings into a clear response.',
    },
  ];
}

function extractAgentMeshMeta(chunks?: RetrievedChunk[]): AgentMeshMessageMeta {
  const meta: AgentMeshMessageMeta = {
    followUps: [],
    toolCalls: [],
    failedTools: [],
  };

  if (!chunks || chunks.length === 0) {
    return meta;
  }

  for (const chunk of chunks) {
    const chunkMeta = chunk.metadata;
    if (!chunkMeta) {
      continue;
    }

    if (Array.isArray(chunkMeta.follow_ups)) {
      meta.followUps = chunkMeta.follow_ups
        .filter((item) => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (Array.isArray(chunkMeta.tool_calls)) {
      meta.toolCalls = chunkMeta.tool_calls.filter((item) => !!item && typeof item === 'object');
    }
    if (Array.isArray(chunkMeta.failed_tools)) {
      meta.failedTools = chunkMeta.failed_tools
        .filter((item) => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return meta;
}

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  metadata?: Record<string, any>;
  retrieved_chunks?: RetrievedChunk[];
  created_at: string;
}

interface Conversation {
  id: number;
  uuid: string;
  title?: string;
  message_count: number;
}

interface RAGFlowConversationViewProps {
  domainId: number;
  domainName: string;
  /** Optional conversation UUID to load; otherwise initializes one empty conversation. */
  conversationId?: string;
  /** Enables live Agent Mesh tool-call progress streaming via SSE. */
  enableAgentMeshLiveProgress?: boolean;
  headerContent?: React.ReactNode;
  disableSourceInteractions?: boolean;
  sourceMetadataMode?: 'default' | 'bv-reviews';
  onBack: () => void;
  /** Called when a new conversation is created, with the UUID */
  onConversationCreated?: (uuid: string) => void;
}

const INTERVIEW_MEMORY_CANVAS_TITLE = 'Interview Memory';
const AGENT_MESH_STARTERS = [
  'Summarize the most important context from my recent meeting notes.',
  'What preferences have I already shared about how updates should be written?',
  'Draft a concise follow-up I could send after a leadership sync.',
];

export default function RAGFlowConversationView(props: RAGFlowConversationViewProps) {
  return (
    <ChatProvider>
      <RAGFlowConversationViewInner {...props} />
    </ChatProvider>
  );
}

function RAGFlowConversationViewInner({
  domainId,
  domainName,
  conversationId,
  enableAgentMeshLiveProgress = false,
  headerContent,
  disableSourceInteractions = false,
  sourceMetadataMode = 'default',
  onBack,
  onConversationCreated,
}: RAGFlowConversationViewProps) {
  const token = localStorage.getItem('auth_token');
  const { push: addToast } = useToasts();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const conversationCreationRef = useRef<{
    domainId: number;
    token: string | null;
    promise: Promise<Conversation>;
  } | null>(null);
  const { setCanvasOpen, setCanvasContent, setCanvasTitle, canvasOpen, canvasTitle } = useChat();

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [liveExecutionSteps, setLiveExecutionSteps] = useState<ExecutionStep[]>([]);
  const [liveProgressLabel, setLiveProgressLabel] = useState('Processing question...');
  const eventSourceRef = useRef<EventSource | null>(null);
  const memoryCompanionOpen =
    enableAgentMeshLiveProgress && canvasOpen && canvasTitle === INTERVIEW_MEMORY_CANVAS_TITLE;

  // Handler for opening PDF in canvas panel.
  // Uses the unified ragflow document endpoint which handles both FASB on-disk
  // PDFs and native RAG documents from object storage.
  const handleShowPdf = useCallback((docId: string, pageNumber: string, highlightText?: string) => {
    setCanvasTitle(`Document ${docId} - Page ${pageNumber}`);
    setCanvasContent(
      <PdfCanvasViewer
        docId={docId}
        pageNumber={pageNumber}
        highlightText={highlightText}
        pdfEndpoint="generic"
      />
    );
    setCanvasOpen(true);
  }, [setCanvasTitle, setCanvasContent, setCanvasOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleOpenMemoryCompanion = useCallback(() => {
    if (!enableAgentMeshLiveProgress) return;
    if (memoryCompanionOpen) {
      setCanvasOpen(false);
      return;
    }
    setCanvasTitle(INTERVIEW_MEMORY_CANVAS_TITLE);
    setCanvasContent(<MemoryCompanionPanel workspaceId={domainId} />);
    setCanvasOpen(true);
  }, [
    domainId,
    enableAgentMeshLiveProgress,
    memoryCompanionOpen,
    setCanvasContent,
    setCanvasOpen,
    setCanvasTitle,
  ]);

  // Cleanup live stream connection on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Initialize conversation - either load existing or create one empty chat.
  // The creation promise survives StrictMode's effect replay, so one page visit
  // produces one server-side conversation instead of two competing POSTs.
  useEffect(() => {
    const abortController = new AbortController();
    let isCancelled = false;

    const initConversation = async () => {
      setIsLoading(true);
      try {
        if (conversationId) {
          // Load specific conversation by UUID
          const res = await fetch(`${API_BASE}/v1/ragflow/conversations/by-uuid/${conversationId}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal,
          });

          if (!isCancelled && res.ok) {
            const data = await res.json();
            setConversation(data.conversation);
            setMessages(data.messages || []);
          } else if (!isCancelled) {
            addToast({ kind: 'error', message: 'Conversation not found' });
            onBack();
          }
        } else {
          // Create a new conversation when no conversationId provided
          let creation = conversationCreationRef.current;
          if (
            !creation
            || creation.domainId !== domainId
            || creation.token !== token
          ) {
            const promise = fetch(
              `${API_BASE}/v1/ragflow/domains/${domainId}/conversations`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({}),
              },
            ).then(async (response) => {
              if (!response.ok) {
                throw new Error(`Conversation creation failed (${response.status})`);
              }
              return response.json() as Promise<Conversation>;
            });
            creation = {
              domainId,
              token,
              promise,
            };
            conversationCreationRef.current = creation;
          }

          const newConv = await creation.promise;
          if (!isCancelled) {
            setConversation(newConv);
            setMessages([]);
            // Notify parent to update URL with the new conversation UUID
            if (newConv.uuid && onConversationCreated) {
              onConversationCreated(newConv.uuid);
            }
          }
        }
      } catch (e) {
        // Ignore AbortError from cleanup — this is expected during StrictMode remount
        if (isCancelled || (e instanceof DOMException && e.name === 'AbortError')) {
          return;
        }
        console.error('Failed to initialize conversation:', e);
        addToast({ kind: 'error', message: 'Failed to start conversation' });
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    initConversation();

    // Cleanup: abort in-flight fetch and prevent state updates after unmount
    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [domainId, conversationId, token, addToast, onBack, onConversationCreated]);

  // Send message
  const sendMessage = useCallback(async (content: string) => {
    if (!conversation || !content.trim()) return;

    const userContent = content.trim();
    const requestId = enableAgentMeshLiveProgress
      ? (
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `agent-mesh-${Date.now()}-${Math.random().toString(16).slice(2)}`
      )
      : undefined;

    setMessageInput('');
    setIsSending(true);
    setLiveExecutionSteps(
      enableAgentMeshLiveProgress
        ? createInitialLiveSteps()
        : [
          {
            id: 'submitted',
            label: 'Working on your request',
            status: 'active',
          },
        ]
    );
    setLiveProgressLabel(enableAgentMeshLiveProgress ? 'Understanding your question' : 'Working on your request...');

    // Add optimistic user message
    const tempUserMsg: Message = {
      id: Date.now(),
      role: 'user',
      content: userContent,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      if (enableAgentMeshLiveProgress && token && requestId) {
        const streamUrl =
          `${API_BASE}/v1/ragflow/domains/${domainId}/conversations/${conversation.id}/agent-mesh/stream` +
          `?token=${encodeURIComponent(token)}` +
          `&request_id=${encodeURIComponent(requestId)}`;

        const eventSource = new EventSource(streamUrl);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
          try {
            const liveEvent = JSON.parse(event.data) as AgentMeshLiveEvent;
            const eventType = typeof liveEvent.event_type === 'string' ? liveEvent.event_type : 'progress';
            const metadata = liveEvent.metadata && typeof liveEvent.metadata === 'object' ? liveEvent.metadata : {};
            setLiveProgressLabel(getAgentMeshFriendlyLiveProgressLabel(eventType, metadata));

            setLiveExecutionSteps((prev) => {
              return reduceAgentMeshLiveEventToSteps(prev, eventType, metadata);
            });

            if (eventType === 'completed' || eventType === 'failed') {
              eventSource.close();
              if (eventSourceRef.current === eventSource) {
                eventSourceRef.current = null;
              }
            }
          } catch (error) {
            console.error('Failed to parse Agent Mesh SSE event:', error);
          }
        };

        eventSource.onerror = () => {
          if (eventSource.readyState === EventSource.CLOSED) {
            eventSource.close();
            if (eventSourceRef.current === eventSource) {
              eventSourceRef.current = null;
            }
          }
        };
      }

      const res = await fetch(
        `${API_BASE}/v1/ragflow/domains/${domainId}/conversations/${conversation.id}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            content: userContent,
            ...(requestId ? { request_id: requestId } : {}),
          }),
        }
      );

      if (res.ok) {
        const data = await res.json();
        // Replace optimistic message and add response
        setMessages(prev => [
          ...prev.slice(0, -1),
          data.user_message,
          data.assistant_message,
        ]);
      } else {
        let errorMsg = 'Failed to send message';
        try {
          const error = await res.json();
          errorMsg = error.detail || errorMsg;
          // Check for specific errors
          if (res.status === 404 || errorMsg.includes('not found')) {
            errorMsg = 'Documents are still being indexed. Please wait a moment and try again.';
          } else if (errorMsg.includes('RAGFlow')) {
            errorMsg = 'RAG service error. Documents may still be processing.';
          }
        } catch {
          if (res.status === 404) {
            errorMsg = 'Documents are still being indexed. Please wait a moment and try again.';
          }
        }
        addToast({ kind: 'error', message: errorMsg });
        // Remove optimistic message
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (e) {
      addToast({ kind: 'error', message: 'Failed to send message' });
      setMessages(prev => prev.slice(0, -1));
      setLiveProgressLabel('Could not complete your request');
      setLiveExecutionSteps((prev) => {
        const activeStep = prev.find((step) => step.status === 'active');
        if (activeStep) {
          return prev.map((step) =>
            step.id === activeStep.id
              ? {
                ...step,
                status: 'failed',
                description: 'The request failed before completion.',
              }
              : step
          );
        }
        return prev;
      });
    } finally {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsSending(false);
      setTimeout(() => {
        setLiveExecutionSteps([]);
      }, 150);
    }
  }, [
    conversation,
    domainId,
    token,
    addToast,
    enableAgentMeshLiveProgress,
  ]);

  // Handle submit from PromptBar
  const handleSubmit = (value: string) => {
    if (!value.trim() || isSending) return;
    sendMessage(value);
  };

  const handleProviderAuthComplete = useCallback(
    async (auth: RuntimeProviderAuthRequired) => {
      const resumeQuery = String((auth as any).resume_query || '').trim();
      if (!resumeQuery) {
        addToast({ kind: 'error', message: 'Authentication completed, but the original question could not be resumed.' });
        return;
      }
      await sendMessage(resumeQuery);
    },
    [addToast, sendMessage],
  );

  const latestAssistantMessageId = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') {
        return messages[i].id;
      }
    }
    return null;
  }, [messages]);

  if (isLoading) {
    return (
      <ChatContainer className="bg-gray-50 dark:bg-dark-bg">
        <ChatMessagesPane>
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading conversation...</p>
          </div>
        </ChatMessagesPane>
      </ChatContainer>
    );
  }

  return (
    <ChatContainer className="bg-gray-50 dark:bg-dark-bg">
      <ChatMessagesPane>
        {/* Minimal header with back button */}
        <div className="absolute top-0 left-0 right-0 z-20 border-b border-stone-200/80 bg-white/90 px-4 py-3 backdrop-blur-sm dark:border-dark-border/30 dark:bg-dark-surface/85">
          <div className="flex items-start gap-3">
            <button
              onClick={onBack}
              className="rounded-md p-1.5 text-gray-400 transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none hover:bg-stone-100 hover:text-stone-700 dark:text-gray-500 dark:hover:bg-dark-surface-2 dark:hover:text-gray-200"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-small font-medium text-stone-700 dark:text-gray-200">{domainName}</span>
                {enableAgentMeshLiveProgress && (
                  <span className="rounded-md bg-stone-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-600 dark:bg-dark-surface-2 dark:text-gray-400">
                    {memoryCompanionOpen ? 'Memory panel open' : 'Chat mode'}
                  </span>
                )}
              </div>
              {enableAgentMeshLiveProgress && (
                <p className="mt-1 text-xs leading-5 text-stone-500 dark:text-gray-400">
                  Ask Agent Mesh here. Capture durable preferences and constraints in the right panel only when they should persist.
                </p>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2 pointer-events-auto">
              {headerContent}
              {enableAgentMeshLiveProgress && (
                <button
                  onClick={handleOpenMemoryCompanion}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-[background-color,border-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none ${
                    memoryCompanionOpen
                      ? 'border-stone-900 bg-stone-900 text-white dark:border-white dark:bg-white dark:text-charcoal'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-800 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300 dark:hover:border-gray-500 dark:hover:text-gray-100'
                  }`}
                >
                  <SparklesIcon className="h-4 w-4" />
                  {memoryCompanionOpen ? 'Interview memory' : 'Open interview memory'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <ChatScrollArea>
          {/* Spacer for header */}
          <div className="h-12 flex-shrink-0" />

          {messages.length === 0 ? (
            <div
              className={`flex h-full text-gray-400 ${
                enableAgentMeshLiveProgress
                  ? 'items-start justify-center pt-16 sm:pt-20'
                  : 'items-center justify-center'
              }`}
            >
              <div className="max-w-lg text-center">
                <SparklesIcon className="w-10 h-10 mb-4 mx-auto opacity-20" />
                <p className="text-sm font-medium text-stone-700 dark:text-gray-200">
                  {enableAgentMeshLiveProgress
                    ? 'Ask Agent Mesh a question, then capture only the details that should still matter next time.'
                    : 'Ask questions about your documents and I’ll provide answers with sources.'}
                </p>
                {enableAgentMeshLiveProgress && (
                  <p className="mt-2 text-xs leading-5 text-stone-500 dark:text-gray-400">
                    {memoryCompanionOpen
                      ? 'Interview memory is open on the right and ready for review.'
                      : 'Open interview memory when a preference, constraint, or standing fact should become reusable context.'}
                  </p>
                )}
                {enableAgentMeshLiveProgress && (
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {AGENT_MESH_STARTERS.map((starter) => (
                      <button
                        key={starter}
                        type="button"
                        onClick={() => handleSubmit(starter)}
                        className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-[background-color,border-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none hover:border-gray-300 hover:bg-gray-50 hover:text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:text-gray-100"
                      >
                        {starter}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <MessageBubble key={msg.id} role={msg.role} showAvatar={msg.role === 'assistant'}>
                <MessageContent>
                  {msg.role === 'assistant' ? (
                    <AssistantMessageContent
                      content={msg.content}
                      metadata={msg.metadata}
                      chunks={msg.retrieved_chunks}
                      onShowPdf={disableSourceInteractions ? undefined : handleShowPdf}
                      onFollowUpClick={handleSubmit}
                      onProviderAuthComplete={handleProviderAuthComplete}
                      isLatestAssistant={msg.id === latestAssistantMessageId}
                      isAgentMesh={enableAgentMeshLiveProgress}
                      disableSourceInteractions={disableSourceInteractions}
                      sourceMetadataMode={sourceMetadataMode}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  )}
                </MessageContent>
              </MessageBubble>
            ))
          )}

          {/* Thinking indicator with RAG flow stages */}
          {isSending && (
            <MessageBubble role="assistant" showAvatar>
              <ThinkingIndicator
                label={liveProgressLabel || 'Processing question...'}
                hideAvatar
                steps={liveExecutionSteps}
                stepsDefaultExpanded={true}
              />
            </MessageBubble>
          )}

          <div ref={chatEndRef} />
        </ChatScrollArea>

        {/* Input */}
        <ChatInputArea>
          <PromptBar
            value={messageInput}
            onChange={setMessageInput}
            onSubmit={handleSubmit}
            placeholder={`Ask a question about ${domainName}...`}
            loading={isSending}
            disabled={!conversation}
          />
        </ChatInputArea>
      </ChatMessagesPane>

      {/* PDF Canvas Panel */}
      {canvasOpen && <CanvasPanel />}
    </ChatContainer>
  );
}

/**
 * Assistant message content with parsed sources and validation.
 * Works uniformly for FASB and native RAG workspaces.
 */
function AssistantMessageContent({
  content,
  chunks,
  metadata,
  onShowPdf,
  onFollowUpClick,
  onProviderAuthComplete,
  isLatestAssistant,
  isAgentMesh,
  disableSourceInteractions = false,
  sourceMetadataMode = 'default',
}: {
  content: string;
  chunks?: RetrievedChunk[];
  metadata?: Record<string, any>;
  onShowPdf?: (docId: string, pageNumber: string, highlightText?: string) => void;
  onFollowUpClick?: (value: string) => void;
  onProviderAuthComplete?: (auth: RuntimeProviderAuthRequired) => Promise<void>;
  isLatestAssistant?: boolean;
  isAgentMesh?: boolean;
  disableSourceInteractions?: boolean;
  sourceMetadataMode?: 'default' | 'bv-reviews';
}) {
  const [debugDetailsOpen, setDebugDetailsOpen] = React.useState(false);
  const isBVReviewsMode = sourceMetadataMode === 'bv-reviews';
  const token = localStorage.getItem('auth_token');

  // Extract Agent Mesh specific metadata from chunks
  const agentMeshMeta = React.useMemo(
    () => extractAgentMeshMeta(chunks),
    [chunks]
  );
  const providerAuthRequired = React.useMemo(
    () => extractProviderAuthRequiredFromMessage(metadata, chunks),
    [metadata, chunks],
  );
  const normalizedContent = React.useMemo(
    () => normalizeAgentMeshAssistantMarkdownContent(content),
    [content]
  );
  const artifacts = React.useMemo(
    () => extractWorkspaceChatArtifactsFromMessage(metadata, chunks),
    [metadata, chunks],
  );
  const presentationContent = React.useMemo(
    () =>
      artifacts.some(
        (artifact) =>
          (artifact.render_kind || artifact.artifact_type) === 'table',
      )
        ? stripGfmTableBlocks(normalizedContent)
        : normalizedContent,
    [artifacts, normalizedContent],
  );

  const executionSteps = React.useMemo(() => {
    if (!agentMeshMeta.toolCalls) return [];
    return agentMeshMeta.toolCalls.map((tc, idx) => ({
      id: tc.id || `step-${idx}`,
      label: tc.label || tc.tool || 'Action',
      status: tc.status || 'completed',
      description: tc.description,
    })) as ExecutionStep[];
  }, [agentMeshMeta.toolCalls]);

  const executionSummaryLabel = React.useMemo(() => {
    const count = agentMeshMeta.toolCalls.length;
    if (count === 0) return '';
    return `${count} tool call${count !== 1 ? 's' : ''} executed`;
  }, [agentMeshMeta.toolCalls]);
  const debugDumps = React.useMemo(
    () =>
      agentMeshMeta.toolCalls
        .filter((tc) => tc.debug_dump)
        .map((tc) => ({
          tool: tc.label || tc.tool || 'Tool call',
          errorCode: tc.error_code,
          dump: tc.debug_dump,
        })),
    [agentMeshMeta.toolCalls],
  );

  const followUpSuggestions = agentMeshMeta.followUps || [];

  const handleValidateCitation = async (index: number): Promise<CitationValidationResult> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const source = sources.find((item) => item.index === index);
    if (!source?.rawCitation || !source.highlightText) {
      throw new Error('Citation evidence is not available for validation.');
    }

    const response = await fetch(`${RAG_EVAL_API_BASE}/citations/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        citation: source.rawCitation,
        chunk_text: source.highlightText,
        answer: mainContent,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Validation failed (${response.status})`);
    }

    const data = await response.json();
    return data.result;
  };

  const configuredSources = React.useMemo(() => {
    const chunk = chunks?.find(c => c.metadata?.configured_sources);
    return (chunk?.metadata?.configured_sources as string[]) || [];
  }, [chunks]);

  const availableSources = React.useMemo(() => {
    const chunk = chunks?.find(c => c.metadata?.available_sources);
    return (chunk?.metadata?.available_sources as string[]) || [];
  }, [chunks]);

  const blockedSources = React.useMemo(() => {
    const chunk = chunks?.find(c => c.metadata?.blocked_sources);
    return (chunk?.metadata?.blocked_sources as string[]) || [];
  }, [chunks]);

  // Parse sources from the text content (FASB format: "\n\nSources:\n[1] doc:...")
  const { mainContent, sources: textParsedSources } = React.useMemo(
    () => parseSourcesFromSummary(presentationContent),
    [presentationContent]
  );

  // Build full sources list, then filter to only those cited in the answer
  const sources: Source[] = React.useMemo(() => {
    const allSources = textParsedSources.length > 0
      ? textParsedSources
      : (!chunks || chunks.length === 0 ? [] : buildSourcesFromChunks(chunks));

    // Extract citation indices referenced in the answer text, e.g. [1], [3]
    const citedIndices = new Set<number>();
    const citationRe = /\[(\d+)\]/g;
    let m: RegExpExecArray | null;
    while ((m = citationRe.exec(mainContent)) !== null) {
      citedIndices.add(parseInt(m[1], 10));
    }

    if (citedIndices.size === 0) return allSources;
    return allSources.filter(s => citedIndices.has(s.index));
  }, [textParsedSources, chunks, mainContent]);
  // Build a map from citation index to source data for quick lookup
  const sourcesMap = React.useMemo(() => {
    const map: Record<number, Source> = {};
    sources.forEach(s => { map[s.index] = s; });
    return map;
  }, [sources]);

  // Helper to render text with clickable citation badges
  const renderWithCitations = React.useCallback((text: string): React.ReactNode => {
    const parts = text.split(/(\[\d+\])/g);
    if (parts.length === 1) return text;

    return parts.map((part, i) => {
      const match = part.match(/\[(\d+)\]/);
      if (match) {
        const citationIndex = parseInt(match[1], 10);
        const source = sourcesMap[citationIndex];

        return (
          <InlineCitation
            key={i}
            number={citationIndex}
            docId={source?.docId}
            pageNumber={source?.pages}
            sectionTitle={source?.sectionTitle}
            interactive={!disableSourceInteractions && !!source && !!onShowPdf}
            onClick={() => {
              if (!disableSourceInteractions && source && onShowPdf) {
                onShowPdf(source.docId, source.pages || "1", source.highlightText || source.sectionTitle);
              }
            }}
          />
        );
      }
      return part;
    });
  }, [sourcesMap, onShowPdf, disableSourceInteractions]);

  // Process all text nodes recursively to add citation styling
  const processNode = React.useCallback((node: React.ReactNode): React.ReactNode => {
    if (typeof node === 'string') {
      return renderWithCitations(node);
    }
    if (Array.isArray(node)) {
      return node.map((child, i) => <React.Fragment key={i}>{processNode(child)}</React.Fragment>);
    }
    if (React.isValidElement(node)) {
      const children = (node.props as { children?: React.ReactNode }).children;
      if (children) {
        return React.cloneElement(node, {}, processNode(children));
      }
    }
    return node;
  }, [renderWithCitations]);

  const markdownComponents = React.useMemo<Components>(() => ({
    p: ({ children }) => <p>{processNode(children)}</p>,
    li: ({ children }) => <li>{processNode(children)}</li>,
    strong: ({ children }) => <strong>{processNode(children)}</strong>,
    em: ({ children }) => <em>{processNode(children)}</em>,
    table: ({ children, ...props }) => (
      <div className="not-prose my-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-border">
        <table
          className="min-w-full divide-y divide-gray-200 dark:divide-dark-border"
          {...props}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className="bg-gray-50 dark:bg-dark-surface-2" {...props}>
        {children}
      </thead>
    ),
    tbody: ({ children, ...props }) => (
      <tbody
        className="divide-y divide-gray-200 bg-white dark:divide-dark-border dark:bg-dark-surface"
        {...props}
      >
        {children}
      </tbody>
    ),
    tr: ({ children, ...props }) => (
      <tr
        className="transition-colors hover:bg-gray-50 dark:hover:bg-dark-surface-2/50"
        {...props}
      >
        {children}
      </tr>
    ),
    th: ({ children, ...props }) => (
      <th
        className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
        {...props}
      >
        {processNode(children)}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td
        className="whitespace-nowrap px-4 py-2.5 align-top text-sm text-charcoal dark:text-gray-200"
        {...props}
      >
        {processNode(children)}
      </td>
    ),
  }), [processNode]);

  if (providerAuthRequired) {
    return (
      <ProviderAuthCard
        auth={providerAuthRequired}
        onAuthenticated={onProviderAuthComplete || (async () => undefined)}
      />
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-4">
      {/* Agent Mesh Source Trace (Task 5) */}
      {isAgentMesh && (configuredSources.length > 0 || blockedSources.length > 0) && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface-2/50">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Agent Mesh Trace
            </h4>
            <div className="flex gap-2">
              <Badge variant="secondary" className="text-[10px]">
                {availableSources.length} Available
              </Badge>
              {blockedSources.length > 0 && (
                <Badge variant="danger" className="text-[10px]">
                  {blockedSources.length} Blocked
                </Badge>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {/* Configured Sources */}
            <div>
              <p className="mb-1 text-[10px] font-medium text-gray-400">Configured Sources</p>
              <div className="flex flex-wrap gap-1.5">
                {configuredSources.map(s => {
                  const def = getAgentMeshSourceDefinition(s as AgentMeshSourceType);
                  const isAvailable = availableSources.includes(s);
                  const isBlocked = blockedSources.includes(s);

                  return (
                    <div
                      key={s}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium",
                        isBlocked
                          ? "border-red-200 bg-red-50 text-red-600 dark:border-red-900/30 dark:bg-red-900/20 dark:text-red-400"
                          : isAvailable
                            ? "border-green-200 bg-green-50 text-green-600 dark:border-green-900/30 dark:bg-green-900/20 dark:text-green-400"
                            : "border-gray-200 bg-gray-50 text-gray-500 dark:border-dark-border dark:bg-dark-surface dark:text-gray-400"
                      )}
                    >
                      {def?.icon && <def.icon className="h-3 w-3" />}
                      {def?.label || s}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Selected Sources (where tools were actually called) */}
            {executionSteps.length > 0 && (
              <div>
                <p className="mb-1 text-[10px] font-medium text-gray-400">Selected & Queried</p>
                <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-2 dark:border-dark-border/50 dark:bg-dark-surface">
                  <ThinkingIndicator
                    label={executionSummaryLabel}
                    steps={executionSteps}
                    stepsDefaultExpanded={false}
                    hideAvatar
                    isComplete
                  />
                  {debugDumps.length > 0 && (
                    <div className="mt-3 border-t border-gray-200 pt-3 dark:border-dark-border">
                      <button
                        type="button"
                        onClick={() => setDebugDetailsOpen((open) => !open)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 transition hover:border-violet-300 hover:text-violet-700 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300 dark:hover:border-violet-500 dark:hover:text-violet-300"
                      >
                        <BugAntIcon className="h-3.5 w-3.5" />
                        SQL debug
                      </button>
                      {debugDetailsOpen && (
                        <div className="mt-2 space-y-2 rounded-md border border-gray-200 bg-white p-2 text-[11px] text-gray-600 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                          {debugDumps.map((item, index) => (
                            <div key={`${item.dump?.dump_id || item.dump?.filename || index}`} className="space-y-1">
                              <div className="font-semibold text-charcoal dark:text-white">
                                {item.tool}
                                {item.errorCode ? ` (${item.errorCode})` : ''}
                              </div>
                              {item.dump?.path && (
                                <div className="break-all font-mono">{item.dump.path}</div>
                              )}
                              {item.dump?.filename && !item.dump?.path && (
                                <div className="break-all font-mono">{item.dump.filename}</div>
                              )}
                              {item.dump?.timestamp && (
                                <div className="text-gray-400 dark:text-gray-500">{item.dump.timestamp}</div>
                              )}
                              {item.dump?.retrieval_context && (
                                <div className="mt-2 space-y-2 border-t border-gray-100 pt-2 dark:border-dark-border">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <Badge variant="secondary" className="text-[10px]">
                                      {item.dump.retrieval_context.retrieval_backend || item.dump.retrieval_context.retrieval_mode || 'retrieval'}
                                    </Badge>
                                    {item.dump.retrieval_context.fallback_used && (
                                      <Badge variant="warning" className="text-[10px]">
                                        fallback
                                      </Badge>
                                    )}
                                  </div>
                                  {(item.dump.retrieval_context.selected_chunks || []).length > 0 && (
                                    <div className="space-y-1">
                                      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                        Selected chunks
                                      </div>
                                      {(item.dump.retrieval_context.selected_chunks || []).slice(0, 6).map((chunk, chunkIndex) => (
                                        <div key={`${chunk.fragment_id || chunkIndex}`} className="rounded border border-gray-100 bg-gray-50 px-2 py-1 dark:border-dark-border dark:bg-dark-surface">
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="truncate font-mono text-[10px] text-charcoal dark:text-white">
                                              {chunk.fragment_id || chunk.canonical_name || chunk.document_type}
                                            </span>
                                            {typeof chunk.score === 'number' && (
                                              <span className="shrink-0 font-mono text-[10px] text-gray-400">
                                                {chunk.score.toFixed(1)}
                                              </span>
                                            )}
                                          </div>
                                          {(chunk.match_reasons || []).length > 0 && (
                                            <div className="mt-0.5 line-clamp-2 text-[10px] text-gray-500 dark:text-gray-400">
                                              {(chunk.match_reasons || []).join('; ')}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {(item.dump.retrieval_context.deterministic_expansion_reasons || []).length > 0 && (
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                                      Expansion: {(item.dump.retrieval_context.deterministic_expansion_reasons || [])[0]?.reason}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content with markdown rendering */}
      <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-headings:my-3 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-pre:my-2 prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-code:bg-gray-100 dark:prose-code:bg-dark-surface-2 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none prose-pre:bg-gray-900 dark:prose-pre:bg-black prose-pre:text-gray-100 prose-a:text-violet-600 dark:prose-a:text-violet-400 prose-strong:text-charcoal dark:prose-strong:text-white">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {mainContent}
        </ReactMarkdown>
      </div>

      <WorkspaceChatArtifactBlocks
        artifacts={artifacts}
        onFollowUpClick={onFollowUpClick}
      />

      {/* Citations / Sources Accordion */}
      {!isAgentMesh && sources.length > 0 && (
        <SourcesAccordion
          sources={sources}
          onValidateCitation={handleValidateCitation}
          onShowPdf={onShowPdf}
          showPdfButtons={!disableSourceInteractions && !!onShowPdf}
          showValidateButtons={true}
          interactiveSourceLinks={!disableSourceInteractions}
          showReviewMetadata={isBVReviewsMode}
        />
      )}

      {/* Follow-up Suggestions */}
      {isLatestAssistant && onFollowUpClick && followUpSuggestions.length > 0 && (
        <div className="pt-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
            <SparklesIcon className="h-3 w-3" />
            Suggested follow-up questions
          </p>
          <div className="flex flex-wrap gap-2">
            {followUpSuggestions.map((question, index) => (
              <button
                key={`${question}-${index}`}
                onClick={() => onFollowUpClick(question)}
                className="text-left text-xs px-3 py-1.5 rounded-full border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface-2 text-gray-600 dark:text-gray-300 transition-all active:scale-[0.97] hover:border-violet-300 dark:hover:border-violet-500 hover:text-violet-700 dark:hover:text-violet-300"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
